import React, { useEffect, useRef } from "react";

const SmokeEffect = () => {
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctxRef.current = ctx;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const particles = [];
    const particleCount = 150; // Increased for denser smoke

    // Particle class
    class Particle {
      constructor(x, y) {
        this.x = x || Math.random() * canvas.width;
        this.y = y || Math.random() * canvas.height;

        this.size = Math.random() * 8 + 3; // Larger particles for smoke effect
        this.speedX = Math.random() * 0.5 - 0.25;
        this.speedY = Math.random() * 0.5 - 0.25;
        this.opacity = Math.random() * 0.6 + 0.2;
        this.color = `rgba(230, 230, 250, ${this.opacity})`; // Dark lavender base
      }

      update(mouse) {
        // Move towards mouse with some randomness
        if (mouse.x && mouse.y) {
          const dx = mouse.x - this.x;
          const dy = mouse.y - this.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          if (distance < 150) {
            this.x += dx / 30;
            this.y += dy / 30;
            this.opacity = Math.min(0.9, this.opacity + 0.02); // Lavender overpowering
          } else {
            this.x += this.speedX;
            this.y += this.speedY;
          }
        }

        // Bounce off edges with fade
        if (this.x < 0 || this.x > canvas.width) this.speedX *= -0.9;
        if (this.y < 0 || this.y > canvas.height) this.speedY *= -0.9;

        this.opacity *= 0.97; // Gradual fade
      }

      draw() {
        ctx.fillStyle = this.color;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // Initialize particles
    for (let i = 0; i < particleCount; i++) {
      particles.push(new Particle());
    }

    let mouse = { x: null, y: null };

    // Mouse move event
    canvas.addEventListener("mousemove", (e) => {
      mouse.x = e.x;
      mouse.y = e.y;
      for (let i = 0; i < 3; i++) {
        particles.push(new Particle(mouse.x, mouse.y));
      }
    });

    // Animation loop
    function animate() {
      // Background gradient: dark lavender to white
      const gradient = ctx.createRadialGradient(
        canvas.width / 2,
        canvas.height / 2,
        0,
        canvas.width / 2,
        canvas.height / 2,
        canvas.width / 2
      );
      gradient.addColorStop(0, "rgba(230, 230, 250, 0.9)"); // Dark lavender center
      gradient.addColorStop(1, "rgba(255, 255, 255, 0.3)"); // Faded white edge
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.globalAlpha = 0.4;

      particles.forEach((particle, index) => {
        particle.update(mouse);
        particle.draw();

        // Remove faded particles and add new ones
        if (particle.opacity < 0.1) {
          particles.splice(index, 1);
          particles.push(new Particle());
        }
      });

      requestAnimationFrame(animate);
    }

    animate();

    // Cleanup
    return () => {
      canvas.removeEventListener("mousemove", () => {});
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        zIndex: -1,
      }}
    />
  );
};

export default SmokeEffect;
